import { useEffect, useState, type RefObject } from 'react';

/**
 * Whether a rail has anything left to scroll to, re-measured as it resizes.
 *
 * Both home page rails lay out as a plain grid once there is room for every
 * item, and stop scrolling at that point. What is scrollable is therefore a
 * question about the element, not about a breakpoint - and both answers depend
 * on it: an arrow that can move nothing is a control that lies, and a tab stop
 * on a region that cannot scroll is a keypress spent on nothing.
 */
export function useOverflowing(ref: RefObject<HTMLElement | null>) {
  const [overflowing, setOverflowing] = useState(false);

  useEffect(() => {
    const node = ref.current;
    // jsdom has no ResizeObserver and lays nothing out, so tests see `false` -
    // the same answer a screen wide enough for the whole rail would give.
    if (!node || typeof ResizeObserver === 'undefined') return;

    const measure = () => setOverflowing(node.scrollWidth - node.clientWidth > 1);
    measure();

    const observer = new ResizeObserver(measure);
    observer.observe(node);
    return () => observer.disconnect();
  }, [ref]);

  return overflowing;
}
